import React, { useState } from 'react';
import api from '../api';
import { notify } from '../components/CustomToast';
import { useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaQuoteLeft } from 'react-icons/fa';
import Logo from '../assets/logo.png';
import useTitle from '../hooks/useTitle';
import { useI18n } from '../i18n';

const ForgotPassword = () => {
    const { t } = useI18n();
    useTitle('pages.forgot_password.meta_title');

    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        email: '',
        otp: '',
        password: '',
        password_confirmation: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: null });
        }
    };

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const response = await api.post('/forgot-password', { email: formData.email });
            if (response.status) {
                notify('success', response.message || t('messages.auth.otp_sent'));
                setStep(2);
            }
        } catch (err) {
            if (err.status === 422) {
                setErrors(err.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const response = await api.post('/verify-otp', {
                email: formData.email,
                otp: formData.otp
            });
            if (response.status) {
                notify('success', response.message || t('messages.auth.otp_verified'));
                setStep(3);
            }
        } catch (err) {
            if (err.status === 422) {
                setErrors(err.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const response = await api.post('/reset-password', formData);
            if (response.status) {
                notify('success', response.message || t('messages.auth.password_reset_success'));
                setTimeout(() => navigate('/login'), 2000);
            }
        } catch (err) {
            if (err.status === 422) {
                setErrors(err.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-left">
                <div className="left-decoration"></div>
                <div className="login-left-content">
                    <div className="brand-logo">
                        <img src={Logo} alt={t('common.app_name')} style={{ height: '50px' }} />
                    </div>

                    <div className="quote-section">
                        <FaQuoteLeft className="quote-icon" />
                        <p className="quote-text">{t('pages.forgot_password.left_quote')}</p>
                        <div className="testimonial-user">
                            {t('pages.forgot_password.left_quote_author')} <FaCheckCircle className="verified-badge" />
                        </div>
                    </div>

                    <div className="left-footer">
                        <Link to="/login" style={{ textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button className="glass-btn">
                                <FaArrowLeft style={{ marginRight: '8px' }} /> {t('pages.forgot_password.back_to_login')}
                            </button>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="login-right">
                <div className="login-form-container">

                    {step === 1 && (
                        <>
                            <h1 className="login-title">{t('pages.forgot_password.title')}</h1>
                            <p className="login-subtitle">{t('pages.forgot_password.subtitle')}</p>

                            <form onSubmit={handleSendOtp} noValidate>
                                <div className="form-group">
                                    <label className="form-label">{t('common.labels.email_address')}<span className="required">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                                        placeholder={t('forms.placeholders.enter_email')}
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                    {errors.email && <div className="field-error">{errors.email[0]}</div>}
                                </div>
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border" role="status" aria-hidden="true"></span>
                                            <span>{t('common.status.sending')}</span>
                                        </>
                                    ) : t('forms.buttons.send_otp')}
                                </button>
                            </form>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <h1 className="login-title">{t('pages.forgot_password.verify_title')}</h1>
                            <p className="login-subtitle">{t('pages.forgot_password.verify_subtitle', { email: formData.email })}</p>

                            <form onSubmit={handleVerifyOtp} noValidate>
                                <div className="form-group">
                                    <label className="form-label">{t('common.labels.otp_code')}<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="otp"
                                        className={`form-input ${errors.otp ? 'is-invalid' : ''}`}
                                        placeholder={t('forms.placeholders.enter_otp')}
                                        value={formData.otp}
                                        onChange={handleChange}
                                    />
                                    {errors.otp && <div className="field-error">{errors.otp[0]}</div>}
                                </div>
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border" role="status" aria-hidden="true"></span>
                                            <span>{t('common.status.verifying')}</span>
                                        </>
                                    ) : t('forms.buttons.verify_otp')}
                                </button>
                                <p className="ui-font-14" style={{ marginTop: '20px', color: '#666', textAlign: 'center' }}>
                                    {t('forms.helpers.did_not_get_code')} <span onClick={() => setStep(1)} style={{ color: '#1054D6', cursor: 'pointer', fontWeight: 'bold' }}>{t('common.actions.resend')}</span>
                                </p>
                            </form>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <h1 className="login-title">{t('pages.forgot_password.reset_title')}</h1>
                            <p className="login-subtitle">{t('pages.forgot_password.reset_subtitle')}</p>

                            <form onSubmit={handleResetPassword} noValidate>
                                <div className="form-group">
                                    <label className="form-label">{t('common.labels.new_password')}<span className="required">*</span></label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                                            placeholder={t('forms.placeholders.enter_new_password')}
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        <span
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{ top: '14px', right: '15px' }}
                                        >
                                            {showPassword ? t('common.actions.hide') : t('common.actions.show')}
                                        </span>
                                    </div>
                                    {errors.password && <div className="field-error">{errors.password[0]}</div>}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('common.labels.confirm_password')}<span className="required">*</span></label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="password_confirmation"
                                            className={`form-input ${errors.password_confirmation ? 'is-invalid' : ''}`}
                                            placeholder={t('forms.placeholders.confirm_new_password')}
                                            value={formData.password_confirmation}
                                            onChange={handleChange}
                                        />
                                        <span
                                            className="password-toggle"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            style={{ top: '14px', right: '15px' }}
                                        >
                                            {showConfirmPassword ? t('common.actions.hide') : t('common.actions.show')}
                                        </span>
                                    </div>
                                </div>
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border" role="status" aria-hidden="true"></span>
                                            <span>{t('common.status.resetting')}</span>
                                        </>
                                    ) : t('forms.buttons.reset_password')}
                                </button>
                            </form>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
